// Shadow DOM stylesheet for <sanctum-form>.
//
// Declares the 25 --sf-* custom properties (Tailwind stone-ish defaults)
// that form the public styling API. Embedders override by setting the
// same custom properties on the host element via their own page CSS.
//
// Naming: the --sf-* prefix is the public API forever once shipped; it
// matches industry precedent (design-system tokens) and does not leak
// internal terminology.

export const STYLES = `
:host {
  /* Typography */
  --sf-font-family: inherit;

  /* Labels */
  --sf-label-color: #44403c;
  --sf-label-size: 0.75rem;
  --sf-label-weight: 600;
  --sf-label-tracking: 0.08em;
  --sf-label-transform: uppercase;

  /* Inputs */
  --sf-input-bg: transparent;
  --sf-input-color: #1c1917;
  --sf-input-border: 1px solid #d6d3d1;
  --sf-input-border-focus: 1px solid #1c1917;
  --sf-input-radius: 0;
  --sf-input-padding: 0.75rem 0;
  --sf-input-font-size: 1rem;

  /* Submit */
  --sf-submit-bg: #1c1917;
  --sf-submit-color: #fafaf9;
  --sf-submit-hover-bg: #44403c;
  --sf-submit-padding: 0.875rem 1.5rem;
  --sf-submit-tracking: 0.1em;
  --sf-submit-radius: 0;
  --sf-submit-font-size: 0.875rem;

  /* Layout */
  --sf-gap: 1.5rem;
  --sf-row-gap: 0.5rem;

  /* States */
  --sf-error-color: #b91c1c;
  --sf-success-color: #166534;
  --sf-disabled-color: #a8a29e;

  /* Sections */
  --sf-section-border: #d6d3d1;
  --sf-section-title-color: #44403c;
  --sf-section-title-size: 1.125rem;
  --sf-section-title-weight: 600;
  --sf-section-title-tracking: 0.02em;
  --sf-section-title-transform: none;

  /* Opt the shadow root into the embedder's colour-scheme context so UA
     widgets (native select option popover, scrollbars, autofill surfaces)
     paint correctly when the embedding document is in dark mode. Without
     this, Chromium does not propagate color-scheme across the shadow DOM
     boundary and the popover paints light-mode defaults regardless.

     NB: must be inherit, not "light dark". "light dark" means "I support
     both schemes, UA picks by user OS preference" — which ignores an
     embedder that forces a scheme via their own document-level
     color-scheme: dark. inherit pulls the host's computed value across
     the shadow boundary, so themed embedders get the right scheme
     regardless of OS preference. */
  color-scheme: inherit;

  display: block;
  font-family: var(--sf-font-family);
  color: var(--sf-input-color);
}

form[part="form"] {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--sf-gap);
}

/* Mobile collapse: every field stacks single-column regardless of the
   per-field width hint (#3091). Consumer media queries cannot reach
   into the shadow DOM, so the breakpoint is enforced here. */
@media (max-width: 540px) {
  form[part="form"] {
    grid-template-columns: 1fr;
  }
  div[part="field"] {
    grid-column: 1 / -1 !important;
  }
}

div[part="section-header"] {
  grid-column: 1 / -1;
  border-top: 1px solid var(--sf-section-border, #d6d3d1);
  padding-top: var(--sf-gap);
  margin-top: 0.5rem;
}

h2[part="section-header-title"] {
  margin: 0;
  font-size: var(--sf-section-title-size, 1.125rem);
  font-weight: var(--sf-section-title-weight, 600);
  color: var(--sf-section-title-color, var(--sf-label-color));
  letter-spacing: var(--sf-section-title-tracking, 0.02em);
  text-transform: var(--sf-section-title-transform, none);
}

div[part="field"] {
  display: flex;
  flex-direction: column;
  gap: var(--sf-row-gap);
}

div[part="field"][hidden] {
  display: none !important;
}

/* ── Save status indicators ── */
div[part="field-footer"] { height: 24px; display: flex; align-items: center; justify-content: flex-end; padding: 0 2px; }
span[part="save-status"] { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; line-height: 1; opacity: 0; transition: opacity 0.2s; }
span[part="save-status"].visible { opacity: 1; }
span[part="save-status"].state-unsaved span[part="save-dot"] { background: #a8a29e; }
span[part="save-status"].state-unsaved span[part="save-label"] { color: #a8a29e; }
span[part="save-status"].state-saving span[part="save-dot"] { background: #d97706; animation: sf-pulse 1s ease-in-out infinite; }
span[part="save-status"].state-saving span[part="save-label"] { color: #d97706; }
span[part="save-status"].state-saved span[part="save-dot"] { background: #15803d; }
span[part="save-status"].state-saved span[part="save-label"] { color: #15803d; }
span[part="save-dot"] { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; display: inline-block; transition: background 0.25s; }
span[part="save-label"] { white-space: nowrap; transition: color 0.25s; }
@keyframes sf-pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }

label[part="label"] {
  color: var(--sf-label-color);
  font-size: var(--sf-label-size);
  font-weight: var(--sf-label-weight);
  letter-spacing: var(--sf-label-tracking);
  text-transform: var(--sf-label-transform);
}

span[part="required-marker"] {
  color: var(--sf-error-color);
  margin-left: 0.15em;
}

input[part="input"],
textarea[part="input"],
select[part="input"] {
  background: var(--sf-input-bg);
  color: var(--sf-input-color);
  border: none;
  border-bottom: var(--sf-input-border);
  border-radius: var(--sf-input-radius);
  padding: var(--sf-input-padding);
  font: inherit;
  font-size: var(--sf-input-font-size);
  outline: none;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 150ms ease;
}

input[part="input"]:focus,
textarea[part="input"]:focus,
select[part="input"]:focus {
  border-bottom: var(--sf-input-border-focus);
}

textarea[part="input"] {
  resize: vertical;
  min-height: 6rem;
}

/* Native <select> popover options — force system colours that follow
   color-scheme. Without this, Chromium paints the popover panel with
   light-mode defaults even when the shadow root inherits color-scheme:
   dark, producing unreadable options (near-white text on white panel)
   for embedders that theme --sf-input-color for dark mode. Canvas and
   CanvasText flip automatically with the inherited colour-scheme. */
option {
  background-color: Canvas;
  color: CanvasText;
}

div[part="checkbox-row"] {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

div[part="checkbox-row"] input[part="input"] {
  width: auto;
  border-bottom: none;
  padding: 0;
  accent-color: var(--sf-submit-bg);
}

div[part="checkbox-row"] label[part="label"] {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 400;
  font-size: 0.875rem;
  color: var(--sf-input-color);
  cursor: pointer;
}

/* Toggle switch (role=switch checkbox) */
input[type="checkbox"][role="switch"] {
  appearance: none;
  -webkit-appearance: none;
  width: 2.75rem;
  height: 1.5rem;
  background: #d6d3d1;
  border-radius: 1.5rem;
  border: none;
  padding: 0;
  position: relative;
  cursor: pointer;
  transition: background 0.15s ease;
  flex-shrink: 0;
}

input[type="checkbox"][role="switch"]::after {
  content: "";
  position: absolute;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: #fff;
  top: 0.125rem;
  left: 0.125rem;
  transition: transform 0.15s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

input[type="checkbox"][role="switch"]:checked {
  background: var(--sf-submit-bg);
}

input[type="checkbox"][role="switch"]:checked::after {
  transform: translateX(1.25rem);
}

input[type="checkbox"][role="switch"]:focus-visible {
  outline: 2px solid var(--sf-submit-bg);
  outline-offset: 2px;
}

/* Radio group */
div[part="radio-group"] {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label[part="radio-option"] {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--sf-input-color);
  cursor: pointer;
}

label[part="radio-option"] input[part="input"] {
  width: auto;
  accent-color: var(--sf-submit-bg);
  padding: 0;
  border-bottom: none;
  margin: 0;
}

/* Checkbox group */
div[part="checkbox-group"] {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label[part="checkbox-option"] {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--sf-input-color);
  cursor: pointer;
}

label[part="checkbox-option"] input[part="input"] {
  width: auto;
  accent-color: var(--sf-submit-bg);
  padding: 0;
  border-bottom: none;
  margin: 0;
}

/* Ordered list — sequential click-to-rank UX */
div[part="ordered-list"] {
  display: flex;
  flex-direction: column;
  gap: 0;
}

div[part="ordered-list-section"] {
  margin-bottom: 1rem;
}

div[part="ordered-list-section"]:last-child {
  margin-bottom: 0;
}

span[part="ordered-list-section-label"] {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--sf-input-color);
  opacity: 0.6;
  margin-bottom: 0.5rem;
}

/* Ranked list */
div[part="ranked-list"] {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: 2px;
  position: relative;
}

/* Pool list */
div[part="pool-list"] {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

/* Pool hint (empty state text) */
div[part="pool-hint"] {
  font-size: 0.8125rem;
  color: var(--sf-input-color);
  opacity: 0.5;
  font-style: italic;
  padding: 0.5rem 0;
}

/* ─── Ranked cards ─── */
div[part="ranked-card"] {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--sf-input-border-color, #d4cec4);
  border-radius: 3px;
  margin-bottom: 0.375rem;
  user-select: none;
  position: relative;
  transition: margin-top 0.2s ease, margin-bottom 0.2s ease, opacity 0.2s ease, border-color 0.15s;
  background: var(--sf-input-bg);
}

div[part="ranked-card"]:hover {
  border-color: var(--sf-submit-bg, #8a6d3b);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

div[part="ranked-card"].dragging {
  opacity: 0.3;
}

div[part="ranked-card"].gap-above {
  margin-top: 1.5rem;
}

div[part="ranked-card"].gap-below {
  margin-bottom: 1.5rem;
}

/* Rank badge */
span[part="rank-badge"] {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--sf-submit-bg, #8a6d3b);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* Grip icon */
span[part="grip-icon"] {
  flex-shrink: 0;
  color: var(--sf-input-color);
  opacity: 0.35;
  font-size: 0.875rem;
  letter-spacing: -0.1em;
  user-select: none;
  line-height: 1;
}

/* Card text */
span[part="card-text"] {
  flex: 1;
  font-size: 0.875rem;
  color: var(--sf-input-color);
  line-height: 1.4;
}

/* Remove button */
button[part="remove-btn"] {
  flex-shrink: 0;
  width: 1.375rem;
  height: 1.375rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  line-height: 1;
  color: var(--sf-input-color);
  opacity: 0.5;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, opacity 0.15s;
  background: none;
  border: none;
  font-family: inherit;
  padding: 0;
}

button[part="remove-btn"]:hover {
  color: #c0392b;
  background: #fbe9e7;
  opacity: 1;
}

/* ─── Pool cards ─── */
div[part="pool-card"] {
  display: flex;
  align-items: center;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--sf-input-border-color, #e3ddd1);
  border-radius: 3px;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, background 0.15s;
  background: var(--sf-input-bg);
}

div[part="pool-card"]:hover {
  border-color: var(--sf-submit-bg, #8a6d3b);
  background: var(--sf-input-bg);
}

:host-context([data-theme="dark"]) div[part="pool-card"]:hover {
  background: var(--sf-input-bg);
  border-color: var(--sf-submit-bg, #8a6d3b);
}

div[part="pool-card"]:focus-visible {
  outline: 2px solid var(--sf-submit-bg, #8a6d3b);
  outline-offset: 1px;
}

/* ─── Drag ghost ─── */
div[part="drag-ghost"] {
  position: fixed;
  pointer-events: none;
  z-index: 999;
  display: none;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--sf-submit-bg, #8a6d3b);
  border-radius: 3px;
  background: var(--sf-input-bg);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.14);
  font-size: 0.875rem;
  opacity: 0.95;
  transform: translate(-50%, -50%);
}

div[part="drag-ghost"] span[part="rank-badge"] {
  width: 1.5rem;
  height: 1.5rem;
  font-size: 0.75rem;
}

div[part="drag-ghost"] span[part="card-text"] {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ─── Drop indicator line ─── */
div[part="drop-indicator"] {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--sf-submit-bg, #8a6d3b);
  border-radius: 1px;
  pointer-events: none;
  z-index: 5;
  transition: opacity 0.15s ease;
  opacity: 0;
}

div[part="drop-indicator"].visible {
  opacity: 1;
}

div[part="drop-indicator"]::before {
  content: "";
  position: absolute;
  left: -4px;
  top: -3px;
  width: 8px;
  height: 8px;
  background: var(--sf-submit-bg, #8a6d3b);
  border-radius: 50%;
}

/* Combobox wrapper (full-width input + hidden datalist) */
div[part="combobox-wrapper"] {
  position: relative;
  width: 100%;
}

div[part="combobox-wrapper"] input[part="input"] {
  width: 100%;
}

/* Multi-select (<select multiple>) — constrain minimum height so
   several options are visible without scrolling when possible. */
select[part~="multi"] {
  min-height: 6rem;
}

/* Rating stars */
div[part="rating-group"] {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

button[part~="rating-star"] {
  all: unset;
  cursor: pointer;
  font-size: 1.5rem;
  line-height: 1;
  color: #d6d3d1;
  transition: color 0.1s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

button[part~="rating-star"][aria-checked="true"],
button[part~="active"] {
  color: #f5a623;
}

button[part~="rating-star"]:hover {
  color: #e69500;
}

button[part~="rating-star"]:focus-visible {
  outline: 2px solid var(--sf-submit-bg);
  outline-offset: 2px;
  border-radius: 2px;
}

button[part~="rating-star"]:disabled {
  color: var(--sf-disabled-color);
  cursor: not-allowed;
}

/* Signature pad */
div[part="signature-wrapper"] {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

canvas[part="signature-canvas"] {
  border: var(--sf-input-border);
  border-radius: var(--sf-input-radius);
  background: #fafaf9;
  width: 100%;
  height: auto;
  max-width: 400px;
  cursor: crosshair;
  touch-action: none;
}

canvas[part="signature-canvas"]:focus-visible {
  outline: 2px solid var(--sf-submit-bg);
  outline-offset: 2px;
}

div[part="signature-toolbar"] {
  display: flex;
  justify-content: flex-end;
}

button[part="signature-clear"] {
  background: none;
  border: 1px solid #d6d3d1;
  color: var(--sf-input-color);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.25rem 0.75rem;
  border-radius: var(--sf-input-radius);
  cursor: pointer;
  transition: border-color 0.15s;
}

button[part="signature-clear"]:hover {
  border-color: #1c1917;
}

button[part="signature-clear"]:focus-visible {
  outline: 2px solid var(--sf-submit-bg);
  outline-offset: 2px;
}

div[part="field-error"] {
  color: var(--sf-error-color);
  font-size: 0.8125rem;
  min-height: 0;
}

div[part="field-error"]:empty {
  display: none;
}

div[part="form-error-banner"] {
  color: var(--sf-error-color);
  font-size: 0.875rem;
}

div[part="form-error-banner"]:empty {
  display: none;
}

section[part="challenge"] {
  grid-column: 1 / -1;
  display: grid;
  gap: 0.75rem;
}

section[part="challenge"][hidden] {
  display: none;
}

p[part="challenge-message"] {
  margin: 0;
  color: var(--sf-input-color);
  font-size: 0.875rem;
}

div[part="challenge-widget"] {
  min-height: 65px;
  width: 100%;
}

div[part="form-error"] {
  background: rgba(185, 28, 28, 0.05);
  border: 1px solid rgba(185, 28, 28, 0.3);
  color: var(--sf-error-color);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
}

button[part="submit"] {
  background: var(--sf-submit-bg);
  color: var(--sf-submit-color);
  border: none;
  border-radius: var(--sf-submit-radius);
  padding: var(--sf-submit-padding);
  font: inherit;
  font-size: var(--sf-submit-font-size);
  font-weight: 600;
  letter-spacing: var(--sf-submit-tracking);
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 150ms ease;
  /* Grid replaces the form's flex layout (#3091); align-self has no
     effect on grid items, so use justify-self to keep the button
     hugging the left edge. Also span both columns so it sits below
     the field grid as a single row. */
  grid-column: 1 / -1;
  justify-self: start;
}

/* #3105 — submit-button width and alignment. The default
   width=full / alignment=left already maps to the existing
   grid-column: 1 / -1 + justify-self: start above; only the
   non-default values need new rules. */
button[part="submit"].submit-w-half {
  grid-column: span 1;
}
button[part="submit"].submit-a-center {
  justify-self: center;
}
button[part="submit"].submit-a-right {
  justify-self: end;
}

button[part="submit"]:hover:not(:disabled) {
  background: var(--sf-submit-hover-bg);
}

button[part="submit"]:disabled {
  background: var(--sf-disabled-color);
  cursor: not-allowed;
}

div[part="attribution"] {
  /* Span both grid columns so the attribution sits centered below the
     form (text-align: center then centers the link inside the full
     row). Without this it defaults to span 1 and anchors to the left
     column — pre-existing from the #3091 grid migration, surfaced when
     the operator centers the submit button via #3105 submit_alignment. */
  grid-column: 1 / -1;
  font-size: 0.75rem;
  color: var(--sf-disabled-color);
  text-align: center;
  margin-top: 0.5rem;
}

div[part="attribution"] a {
  color: inherit;
  text-decoration: none;
}

div[part="attribution"] a:hover {
  text-decoration: underline;
}

p[part="paused"] {
  color: var(--sf-disabled-color);
  font-style: italic;
  padding: 1rem 0;
}

p[part="success"] {
  color: var(--sf-success-color);
  padding: 1rem 0;
  font-size: 1rem;
}

input[part="honeypot"] {
  position: absolute !important;
  left: -9999px !important;
  top: auto !important;
  width: 1px !important;
  height: 1px !important;
  overflow: hidden !important;
}

div[part="range-wrapper"] {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

div[part="range-wrapper"] input[part="input"] {
  flex: 1;
  border-bottom: none;
  padding: 0;
  accent-color: var(--sf-submit-bg);
  height: 1.25rem;
}

span[part="range-value"] {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--sf-input-color);
  min-width: 2rem;
  text-align: right;
}

/* File upload */
div[part="file-wrapper"] {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

input[type="file"][part="input"] {
  padding: 0;
  border-bottom: none;
  font-size: 0.875rem;
  color: var(--sf-input-color);
  cursor: pointer;
}

input[type="file"][part="input"]::file-selector-button {
  background: var(--sf-submit-bg);
  color: var(--sf-submit-color);
  border: none;
  padding: 0.5rem 1rem;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  border-radius: var(--sf-submit-radius);
  margin-right: 0.75rem;
}

input[type="file"][part="input"]::file-selector-button:hover {
  background: var(--sf-submit-hover-bg);
}

span[part="file-info"] {
  font-size: 0.8125rem;
  color: var(--sf-disabled-color);
  min-height: 1rem;
}
`;
